import { BANSHEE_PATH, PLANS, TELLY } from '@/shared/pricing';

export const SLUG_RE = /^[a-z0-9-]+$/;

export const FRONT_STYLES = {
  'On front page': 'border-success-border bg-success-muted text-success',
  Hidden: 'border-border bg-muted text-muted-foreground',
  'Private link': 'border-highlight/40 bg-highlight/10 text-highlight',
};

export const SALE_STYLES = {
  'On sale': 'border-primary/40 bg-primary/10 text-primary',
  Paused: 'border-warning-border bg-warning-muted text-warning',
};

/** Invitation-only plans: managed here, never on the front page, the /p/ pages or in search. */
export const PRIVATE_KEYS = ['Banshee'];
export const isPrivate = (p) => PRIVATE_KEYS.includes(p?.plan_key_c);

export const frontState = (p) => (isPrivate(p) ? 'Private link' : p?.show_on_home_c ? 'On front page' : 'Hidden');
export const saleState = (p) => (p?.active_c ? 'On sale' : 'Paused');

/** Built-in rows mirror a plan that already has its own pricing in the site. */
export const isBuiltIn = (p) => !!p?.plan_key_c;

export const featureList = (p) =>
  String(p?.features_c ?? '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

export const productPath = (p) => (isPrivate(p) ? BANSHEE_PATH : `/p/${p?.slug_c ?? ''}`);

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\x00-\x7f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Where a built-in plan is picked and priced on the home page. */
export const BUILT_IN_ANCHORS = {
  Demon: '/#plans',
  Wraith: '/#plans',
  Telly: '/#desktop-telly',
  Banshee: BANSHEE_PATH,
};

/** Starting rows for the plans built into the site, written by "Set up built-in plans". */
export const BUILT_IN_SEEDS = {
  Demon: {
    Name: PLANS.Demon.name,
    slug_c: 'demon',
    plan_key_c: 'Demon',
    tagline_c: PLANS.Demon.tagline,
    description_c: PLANS.Demon.blurb,
    features_c: [
      '23,000+ live channels',
      '147,000+ movies and VOD',
      '4K, FHD, HD and SD quality',
      'EPG guide, catch-up TV and PPV events',
      '1 to 5 simultaneous screens',
      'All supported devices',
    ].join('\n'),
    show_on_home_c: true,
    active_c: true,
    sort_c: 1,
  },
  Wraith: {
    Name: PLANS.Wraith.name,
    slug_c: 'wraith',
    plan_key_c: 'Wraith',
    tagline_c: PLANS.Wraith.tagline,
    description_c: PLANS.Wraith.blurb,
    features_c: [
      '46,000+ live channels',
      '180,000+ movies and VOD',
      '4K, FHD, HD and SD quality',
      'Catch-up TV and PPV events',
      'One screen at a time',
      'Premium EPG with 6 and 12 month packages',
    ].join('\n'),
    show_on_home_c: true,
    active_c: true,
    sort_c: 2,
  },
  Telly: {
    Name: TELLY.name,
    slug_c: 'desktop-telly',
    plan_key_c: 'Telly',
    tagline_c: PLANS.Telly.tagline,
    description_c: PLANS.Telly.blurb,
    features_c: TELLY.points.join('\n'),
    show_on_home_c: true,
    active_c: true,
    sort_c: 3,
  },
  Banshee: {
    Name: PLANS.Banshee.name,
    slug_c: 'banshee',
    plan_key_c: 'Banshee',
    tagline_c: PLANS.Banshee.tagline,
    description_c: PLANS.Banshee.blurb,
    features_c: ['1 to 5 simultaneous screens', 'Full Ghost Cable channel lineup', 'On-demand vault', 'All supported devices'].join('\n'),
    show_on_home_c: false,
    active_c: true,
    sort_c: 4,
  },
};

/** Ready-made store items the team can add with one click from Products. Keyed by page address. */
export const STORE_SEEDS = {
  'wraith-epg': {
    Name: 'Wraith EPG',
    slug_c: 'wraith-epg',
    plan_key_c: '',
    tagline_c: 'The full TV guide for your Wraith line',
    description_c:
      'Add the premium TV guide (EPG) to a Wraith line. See what is on now and next on every channel, right in the player you already use. Pay once, no renewals.',
    features_c: [
      'Now and next on every channel',
      'Works in the player you already use',
      'For Wraith 1 and 3 month lines (6 and 12 months already include it)',
      'One payment of $7, no renewals',
    ].join('\n'),
    image_url_c: '',
    price_once_c: 7,
    show_on_home_c: true,
    active_c: true,
    sort_c: 10,
  },
};

/** Visibility of a built-in plan: no row means it shows and sells as before. */
export function builtInState(products, key) {
  const row = (products ?? []).find((p) => p.plan_key_c === key);
  return {
    row,
    onHome: PRIVATE_KEYS.includes(key) ? false : row ? !!row.show_on_home_c && !!row.active_c : true,
    onSale: row ? !!row.active_c : true,
  };
}
